// Unified email sign-in (student-self-signup-prd.md §17.4, D-SSU-24). Families
// confuse the parent and student login pages, so either page accepts either
// account: a student email on the Parent Portal sign-in opens the student's own
// Learn session, and a parent or teacher email on the Learn email tab opens the
// adult session. One email, one account, either page.
//
// Every success response keeps its old envelope and adds
//   { account_type: 'adult' | 'student', session_kind: 'user' | 'kid',
//     landing_path: '/portal' | '/teacher' | '/learn' }.
// Client rule: put the token in the slot named by `session_kind`, then go to
// `landing_path`. An older backend omits the fields; the envelope shape (`kid`
// vs `user`) then decides. A sign-in only replaces the session of its own kind,
// so a parent and a student can still share one browser.

import { getSocket } from '@/lib/ws';
import { useAuthStore } from './authStore';
import type {
  KidLoginResponse,
  PrincipalKind,
  UnifiedSignInFields,
  UnifiedSignInResponse,
  VerifyOtpResponse,
} from './types';

export type SignInLandingPath = '/portal' | '/teacher' | '/learn';

const LANDING_PATHS: readonly SignInLandingPath[] = ['/portal', '/teacher', '/learn'];

export interface SignInResult {
  kind: PrincipalKind;
  /** Where the account's home is. Never a value the server did not allow-list. */
  landingPath: SignInLandingPath;
  response: UnifiedSignInResponse;
}

export function isKidSignIn(
  res: UnifiedSignInResponse,
): res is KidLoginResponse & UnifiedSignInFields {
  if (res.session_kind) return res.session_kind === 'kid';
  return 'kid' in res && !!res.kid;
}

export function isAdultSignIn(
  res: UnifiedSignInResponse,
): res is VerifyOtpResponse & UnifiedSignInFields {
  return !isKidSignIn(res);
}

/** The account's home: the served `landing_path` when allow-listed, else by envelope. */
export function signInLandingPath(res: UnifiedSignInResponse): SignInLandingPath {
  const served = res.landing_path;
  if (served && (LANDING_PATHS as readonly string[]).includes(served)) {
    return served as SignInLandingPath;
  }
  if (isKidSignIn(res)) return '/learn';
  return res.user.role === 'teacher' ? '/teacher' : '/portal';
}

/** Store the token in the slot the server named and report where to go. */
export function acceptSignIn(res: UnifiedSignInResponse): SignInResult {
  const kind: PrincipalKind = isKidSignIn(res) ? 'kid' : 'user';
  useAuthStore.getState().setToken(kind, res.access_token);
  useAuthStore.getState().setBootstrapped(true);
  // Learn listens on the kid socket; the Portal has no socket consumer today.
  if (kind === 'kid') setTimeout(() => getSocket('kid'), 0);
  return { kind, landingPath: signInLandingPath(res), response: res };
}
